import type { BatchSummary } from '@shared/models/BatchSummary';
import { formatLineBatchDate } from '@shared/utils/formatLineBatchDate';
import { sanitizeBatchNumberForDisplay } from '@shared/utils/sanitizeBatchDisplay';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface LineBatchMetaProps {
  batch?: BatchSummary | null;
  batchId?: number | null;
  className?: string;
  /** Namespace i18n (p. ej. `locationordersDetailModule`, `merchandiseV3`) */
  i18nNs?: string;
  /** Clave para la etiqueta del lote */
  lotLabelKey?: string;
  /** Clave para la etiqueta de caducidad */
  expiryLabelKey?: string;
  /** Si es true, no se muestra el fallback `#123` cuando falta número de lote (solo texto de lote si existe `batch_number`). */
  hideNumericIdFallback?: boolean;
}

/**
 * Muestra lote (número o id) y caducidad formateada en chips, alineado con órdenes de ubicación.
 */
export const LineBatchMeta: React.FC<LineBatchMetaProps> = ({
  batch,
  batchId,
  className = '',
  i18nNs = 'locationordersDetailModule',
  lotLabelKey = 'items.line_batch',
  expiryLabelKey = 'items.line_expiry',
  hideNumericIdFallback = false,
}) => {
  const { t } = useTranslation(i18nNs);

  const lotFromApi = batch?.batch_number != null ? sanitizeBatchNumberForDisplay(batch.batch_number) : null;
  const loteValue = lotFromApi != null ? lotFromApi : !hideNumericIdFallback && batchId != null ? `#${batchId}` : null;
  const expiryRaw = batch?.expiry_date;
  const expiryFormatted = expiryRaw ? formatLineBatchDate(expiryRaw) : '';

  if (!loteValue && !expiryFormatted) return null;

  const chipClass =
    'inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs text-foreground transition-colors';

  return (
    <div className={`mt-1.5 flex flex-wrap items-center gap-2 ${className}`}>
      {loteValue && (
        <span className={chipClass} data-testid="line-batch-meta-lot">
          <span className="font-normal text-muted-foreground">{t(lotLabelKey, { defaultValue: 'Lote' })}</span>
          <span className="font-medium tabular-nums text-foreground">{loteValue}</span>
        </span>
      )}
      {expiryFormatted && (
        <span className={chipClass} data-testid="line-batch-meta-expiry">
          <span className="font-normal text-muted-foreground">{t(expiryLabelKey, { defaultValue: 'Caducidad' })}</span>
          <span className="font-medium tabular-nums text-foreground">{expiryFormatted}</span>
        </span>
      )}
    </div>
  );
};
